import { makeVar, useReactiveVar } from '@apollo/client';
import { useBoolean } from '@hover/blueprint';
import { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import type { AiDisplayImageFragment } from 'src/shared/graphql/generated';
import { Route, generatePath } from 'src/shared/route';
import type { ButtonPressed } from 'src/shared/telemetry';
import { logError } from 'src/shared/telemetry/logger';
import { isBrowser } from 'src/shared/utils/environment';
import { withQueryParameters } from 'src/shared/utils/routing';

import { categoryToRouteParams } from '../components/Customize/menu';
import type { CustomizationCategory } from '../constants/customizationCategories';
import type { CustomizeRouteState as RouteState } from '../schemas';
import type { CustomizeRouteQuery } from '../schemas/query';

import { useRoutes } from './useRoutes';

export interface CustomizeHandlerProps {
  image: AiDisplayImageFragment | undefined | null;
  state?: Partial<RouteState>;
  query?: CustomizeRouteQuery;
  category?: CustomizationCategory;
  inspire?: boolean;
}

type Tracking = Partial<ButtonPressed>;

const getTracking = (image: AiDisplayImageFragment) =>
  ({
    ai_display_image_id: image.id,
    ai_original_image_id: image.aiOriginalImageId,
    ai_generated_image_id: image.aiGeneratedImage.id,
    style: image.style ?? undefined,
  } satisfies Tracking);

const isHoldingKeys = makeVar(false);

// TODO: this is a temporary "easter egg" that allows us to hold down the X + C
// keys while clicking the customize button and route to the new customize
// experience route. Remove this once we've migrated to the new experience.
if (isBrowser()) {
  let isXPressed = false;
  let isCPressed = false;

  document.addEventListener('keydown', event => {
    try {
      if (event.key.toLowerCase() === 'x') {
        isXPressed = true;
      }
      if (event.key.toLowerCase() === 'c') {
        isCPressed = true;
      }
    } catch (_) {
      // It seems `event.key` is not available in some environments
      // like the embed for some reason. This code is just for internal
      // testing purposes, so catch and bail if anything goes wrong.
    }

    if (isXPressed && isCPressed) isHoldingKeys(true);
  });

  document.addEventListener('keyup', event => {
    try {
      if (event.key.toLowerCase() === 'x') {
        isXPressed = false;
      }
      if (event.key.toLowerCase() === 'c') {
        isCPressed = false;
      }
    } catch (_) {
      // It seems `event.key` is not available in some environments
      // like the embed for some reason. This code is just for internal
      // testing purposes, so catch and bail if anything goes wrong.
    }

    if (!isXPressed || !isCPressed) isHoldingKeys(false);
  });
}

export const useCustomizeHandler = ({
  category,
  image,
  state,
  query,
  inspire,
}: CustomizeHandlerProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasPreloaded, { on: onPreload }] = useBoolean(false);

  const history = useHistory();
  const { pathname } = useLocation();
  const { load, preload } = useRoutes();

  const routeToNewCustomize = useReactiveVar(isHoldingKeys);

  const customizePathname = useMemo(() => {
    if (!image) return null;
    if (inspire)
      return generatePath(
        withQueryParameters(Route.HOMEOWNER_APP_CUSTOMIZE_INSPIRE.path, query),
        {
          aiDisplayImageId: image.id,
        },
      );

    return generatePath(
      withQueryParameters(
        routeToNewCustomize
          ? Route.HOMEOWNER_APP_CUSTOMIZE_NEW.path
          : Route.HOMEOWNER_APP_CUSTOMIZE.path,
        query,
      ),
      {
        aiDisplayImageId: image.id,
        ...(category ? categoryToRouteParams(category) : {}),
      },
    );
  }, [image, inspire, query, routeToNewCustomize, category]);

  const tracking = useMemo(() => {
    if (!image) return {};

    return getTracking(image);
  }, [image]);

  return {
    customizePathname,
    isLoading,
    tracking,
    preload: useCallback(() => {
      if (customizePathname && !hasPreloaded) {
        onPreload();
        preload(customizePathname);
      }
    }, [customizePathname, hasPreloaded, onPreload, preload]),
    onClick: useCallback(async () => {
      if (!customizePathname) {
        const error = new Error(
          'useCustomizeHandler: attempted to navigate to customize without an image',
        );

        if (__DEV__) {
          throw error;
        } else {
          logError(error);
        }

        return;
      }

      setIsLoading(true);

      try {
        await load(customizePathname);
      } finally {
        const { body } = document;

        history.push(customizePathname, {
          referrer: pathname,
          page: { offset: body.scrollTop, height: body.scrollHeight },
          ...state,
        } satisfies RouteState);

        setIsLoading(false);
      }
    }, [customizePathname, history, load, pathname, state]),
  };
};
